import { Type, reflectComponentType } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivationEnd, Router, ɵEmptyOutletComponent } from '@angular/router';
import {
  Observable,
  distinctUntilChanged,
  filter,
  groupBy,
  map,
  mergeMap,
  of,
  pairwise,
  startWith,
  switchMap,
} from 'rxjs';

export const FIREBASE_EVENT_ORIGIN_KEY = 'firebase_event_origin';
export const FIREBASE_PREVIOUS_SCREEN_CLASS_KEY = 'firebase_previous_class';
export const FIREBASE_PREVIOUS_SCREEN_INSTANCE_ID_KEY = 'firebase_previous_id';
export const FIREBASE_PREVIOUS_SCREEN_NAME_KEY = 'firebase_previous_screen';
export const FIREBASE_SCREEN_CLASS_KEY = 'firebase_screen_class';
export const FIREBASE_SCREEN_INSTANCE_ID_KEY = 'firebase_screen_id';
export const FIREBASE_SCREEN_NAME_KEY = 'firebase_screen';
export const OUTLET_KEY = 'outlet';
export const PAGE_PATH_KEY = 'page_path';
export const PAGE_TITLE_KEY = 'page_title';
export const SCREEN_CLASS_KEY = 'screen_class';
export const SCREEN_NAME_KEY = 'screen_name';
export const SCREEN_VIEW_EVENT = 'screen_view';
export const EVENT_ORIGIN_AUTO = 'auto';
export const SCREEN_INSTANCE_DELIMITER = '#';

interface ScreenViewActivationParams {
  outlet: string;
  page_path: string;
  page_title?: string;
  firebase_event_origin: string;
  firebase_screen: string;
  screen_name: string;
}

interface ScreenViewClassedParams extends ScreenViewActivationParams {
  screen_class: string;
}

export interface ScreenViewParams extends ScreenViewClassedParams {
  firebase_screen_class: string;
  firebase_screen_id: number;
  firebase_previous_class?: string;
  firebase_previous_screen?: string;
  firebase_previous_id?: number;
}

let nextScreenInstanceID = Math.floor(Math.random() * (2 ** 32 - 1)) - 2 ** 31;
const knownScreenInstanceIDs: Record<string, number> = {};

function getScreenInstanceID(params: ScreenViewClassedParams): number {
  const screenInstanceKey = [params.screen_class, params.outlet].join(SCREEN_INSTANCE_DELIMITER);
  if (Object.prototype.hasOwnProperty.call(knownScreenInstanceIDs, screenInstanceKey)) {
    return knownScreenInstanceIDs[screenInstanceKey];
  }
  const ret = nextScreenInstanceID++;
  knownScreenInstanceIDs[screenInstanceKey] = ret;
  return ret;
}

/**
 * Ported from `@angular/fire/analytics`'s internal `ɵscreenViewEvent`. Builds
 * an Observable of `screen_view` event payloads from Router activation events.
 *
 * Differences from upstream:
 * - Replaces deprecated `ComponentFactoryResolver.resolveComponentFactory`
 *   with `reflectComponentType` (Angular 14+ public API).
 * - Stricter typing — emits `ScreenViewParams` instead of a loose record.
 * - Drops Zone scheduling; the consumer subscribes from a zoneless context.
 */
export function screenViewEvent(router: Router, title: Title | null): Observable<ScreenViewParams> {
  const activationEndEvents = router.events.pipe(
    filter((e): e is ActivationEnd => e instanceof ActivationEnd),
  );
  return activationEndEvents.pipe(
    switchMap<ActivationEnd, Observable<ScreenViewClassedParams | null>>((activationEnd) => {
      // The router parser stumbles on empty outlet groups like `/asdf/1(bob://sally:asdf)`;
      // double the slash so it parses cleanly.
      const urlTree = router.parseUrl(
        router.url.replaceAll(/(?:\().+(?:\))/g, (a) => a.replaceAll('://', ':///')),
      );
      const pagePath = urlTree.root.children[activationEnd.snapshot.outlet]?.toString() ?? '';
      const actualSnapshot = router.routerState.root.children.find(
        (it) => it.outlet === activationEnd.snapshot.outlet,
      );
      if (!actualSnapshot) {
        return of(null);
      }
      let actualDeep = actualSnapshot;
      while (actualDeep.firstChild) {
        actualDeep = actualDeep.firstChild;
      }
      const screenName =
        actualDeep.pathFromRoot
          .map((s) => s.routeConfig?.path)
          .filter((it): it is string => !!it)
          .join('/') || '/';
      const params: ScreenViewActivationParams = {
        outlet: activationEnd.snapshot.outlet,
        page_path: `/${pagePath}`,
        firebase_event_origin: EVENT_ORIGIN_AUTO,
        firebase_screen: screenName,
        screen_name: screenName,
      };
      if (title) {
        params.page_title = title.getTitle();
      }

      let component: Type<unknown> | string | null | undefined = actualSnapshot.component;
      if (component) {
        if (component === ɵEmptyOutletComponent) {
          let deepSnapshot = activationEnd.snapshot;
          while (deepSnapshot.firstChild) {
            deepSnapshot = deepSnapshot.firstChild;
          }
          component = deepSnapshot.component;
        }
      } else {
        component = activationEnd.snapshot.component;
      }
      if (typeof component === 'string') {
        return of({ ...params, screen_class: component });
      }
      if (component) {
        const selector = reflectComponentType(component)?.selector ?? '';
        return of({ ...params, screen_class: selector });
      }
      // Lazy-load activations have no component — drop them.
      return of(null);
    }),
    filter((it): it is ScreenViewClassedParams => !!it),
    map((params) => ({
      ...params,
      firebase_screen_class: params.screen_class,
      firebase_screen_id: getScreenInstanceID(params),
    })),
    groupBy((it) => it.outlet),
    mergeMap((group) =>
      group.pipe(
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        startWith<ScreenViewParams | undefined>(undefined),
        pairwise(),
        map(([prior, current]) => {
          if (!current) {
            return current;
          }
          if (!prior) {
            return current;
          }
          return {
            ...current,
            firebase_previous_class: prior.screen_class,
            firebase_previous_screen: prior.screen_name,
            firebase_previous_id: prior.firebase_screen_id,
          };
        }),
        filter((it): it is ScreenViewParams => !!it),
      ),
    ),
  );
}
